import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Gift, ArrowLeft, Search, Filter, Package, Truck, CheckCircle, Star, ShoppingCart, Tag, TestTube2, Menu, X, Palette, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import GiftCustomizationModal from '@/components/GiftCustomizationModal';
import Footer from '@/components/Footer';
import ScrollingLogo from '@/components/ScrollingLogo';

const categories = [
  { value: 'all', label: 'All Categories' },
  { value: 'Executive', label: 'Executive' },
  { value: 'Stationery', label: 'Stationery' },
  { value: 'Wellness', label: 'Wellness' },
  { value: 'Tech', label: 'Tech & Gadgets' },
  { value: 'Gourmet', label: 'Gourmet & Food' },
  { value: 'Home & Lifestyle', label: 'Home & Lifestyle' },
  { value: 'Travel', label: 'Travel' },
  { value: 'Eco-Friendly', label: 'Eco-Friendly' },
];

export default function GiftsPage() {
  const navigate = useNavigate();
  const [gifts, setGifts] = useState([]);
  const [filteredGifts, setFilteredGifts] = useState([]);
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [showVendorDialog, setShowVendorDialog] = useState(false);
  const [showCustomizationModal, setShowCustomizationModal] = useState(false);
  const [selectedGift, setSelectedGift] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [priceRange, setPriceRange] = useState('all');
  const [isScrolled, setIsScrolled] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const [customAmount, setCustomAmount] = useState(1);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout, setAuth } = useAuth();
  
  // Login dialog state
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'customize' or 'quickOrder'
  const [pendingGift, setPendingGift] = useState(null);

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await api.login({ email: loginEmail, password: loginPassword });
      setAuth(response.data.user, response.data.token);
      toast.success('Login successful!');
      setShowLoginDialog(false);
      setLoginEmail('');
      setLoginPassword('');
      
      // Execute pending action after successful login
      if (pendingAction && pendingGift) {
        setSelectedGift(pendingGift);
        if (pendingAction === 'customize') {
          setShowCustomizationModal(true);
        } else if (pendingAction === 'quickOrder') {
          setShowOrderDialog(true);
        }
        setPendingAction(null);
        setPendingGift(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Scroll effect for transparent header
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchGifts();
  }, []);

  useEffect(() => {
    filterGifts();
  }, [gifts, selectedCategory, searchQuery, priceRange]);

  const fetchGifts = async () => {
    try {
      const response = await api.getGifts();
      setGifts(response.data);
    } catch (error) {
      console.error('Failed to fetch gifts:', error);
    }
  };

  const filterGifts = () => {
    let filtered = [...gifts];
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(g => g.category === selectedCategory);
    }
    
    if (searchQuery) {
      filtered = filtered.filter(g => 
        g.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        g.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    if (priceRange !== 'all') {
      const [min, max] = priceRange.split('-').map(Number);
      filtered = filtered.filter(g => g.price >= min && (max ? g.price <= max : true));
    }
    
    setFilteredGifts(filtered);
  };

  const handleOrder = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to order');
      return;
    }

    const formData = new FormData(e.target);
    try {
      const orderData = {
        gift_id: selectedGift.id,
        quantity: parseInt(formData.get('quantity')),
        delivery_name: formData.get('delivery_name'),
        delivery_phone: formData.get('delivery_phone'),
        delivery_address: formData.get('delivery_address'),
      };

      const response = await api.createGiftOrder(orderData);
      toast.success('Order created successfully!');
      setShowOrderDialog(false);

      // Use custom amount if test mode is enabled
      let amount = response.data.total_amount;
      if (testMode && customAmount > 0) {
        amount = customAmount;
        toast.info(`Test Mode: Using custom amount ₹${customAmount}`);
      }

      // Create Razorpay order
      const orderResponse = await api.createRazorpayOrder({
        gift_order_id: response.data.id,
        amount: amount,
        payment_type: 'full',
      });

      // Initialize Razorpay checkout
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: orderResponse.data.amount,
        currency: orderResponse.data.currency,
        name: 'My Corporate Hub',
        description: `Gift Order - ${selectedGift.name}`,
        image: 'https://customer-assets.emergentagent.com/job_07322645-a840-4848-8247-f88636798b30/artifacts/kyqhtx8f_MCH%20Logo-2.png',
        order_id: orderResponse.data.order_id,
        handler: async function (razorpayResponse) {
          try {
            // Verify payment
            await api.verifyRazorpayPayment({
              razorpay_order_id: razorpayResponse.razorpay_order_id,
              razorpay_payment_id: razorpayResponse.razorpay_payment_id,
              razorpay_signature: razorpayResponse.razorpay_signature,
              gift_order_id: response.data.id,
            });
            toast.success('Payment successful!');
            window.location.href = '/payment-success?session_id=' + razorpayResponse.razorpay_order_id;
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: user.name || '',
          email: user.email || '',
          contact: user.phone || '',
        },
        theme: {
          color: '#1e3a5f',
        },
        modal: {
          ondismiss: function() {
            toast.info('Payment cancelled');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Order failed');
    }
  };

  const getCategoryCount = (category) => {
    if (category === 'all') return gifts.length;
    return gifts.filter(g => g.category === category).length;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/80 backdrop-blur-lg border-b border-gray-200 shadow-md' 
            : 'bg-sky-100 backdrop-blur-md border-b border-sky-200 shadow-sm'
        }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_07322645-a840-4848-8247-f88636798b30/artifacts/kyqhtx8f_MCH%20Logo-2.png" 
                alt="My Corporate Hub" 
                className="h-16 w-auto"
              />
              <span className="font-heading text-2xl text-navy font-bold tracking-tight hidden md:block">My Corporate Hub<sup className="text-xs ml-0.5">®</sup></span>
            </Link>
            
            {/* Centered Menus */}
            <div className="hidden md:flex items-center gap-8 absolute left-1/2 transform -translate-x-1/2">
              <Link to="/services" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Services</Link>
              <Link to="/properties" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Properties</Link>
              <Link to="/gifts" className="text-gold font-medium text-sm uppercase tracking-wide">Gifting</Link>
              <Link to="/event-permissions" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Event Permits</Link>
              <Link to="/trainers" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Trainers</Link>
              <Link to="/pricing" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Pricing</Link>
            </div>

            <div className="flex items-center gap-4">
              {/* Mobile Menu Button */}
              <button 
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6 text-navy" /> : <Menu className="w-6 h-6 text-navy" />}
              </button>
              
              {user ? (
                <>
                  <Link to={`/dashboard/${user.role}`} className="hidden md:block">
                    <Button variant="ghost" className="text-navy font-medium">Dashboard</Button>
                  </Link>
                  <Button onClick={logout} variant="outline" className="border-navy text-navy hidden md:flex">Logout</Button>
                </>
              ) : (
                <>
                  <Link to="/?login=true" className="hidden md:block">
                    <Button variant="ghost" className="text-navy font-medium">Sign In</Button>
                  </Link>
                  <Link to="/bulk-rfp/new">
                    <Button className="bg-gold hover:bg-gold-light text-navy-900 font-bold shadow-lg px-4 sm:px-6 whitespace-nowrap text-sm sm:text-base">Get Started</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
          
          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <div className="md:hidden bg-white border-t border-gray-200 py-4 px-4 space-y-2">
              <Link to="/services" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium transition-colors" onClick={() => setMobileMenuOpen(false)}>Services</Link>
              <Link to="/properties" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium transition-colors" onClick={() => setMobileMenuOpen(false)}>Properties</Link>
              <Link to="/gifts" className="block py-3 px-4 text-gold bg-sky-50 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Gifting</Link>
              <Link to="/event-permissions" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium transition-colors" onClick={() => setMobileMenuOpen(false)}>Event Permits</Link>
              <Link to="/trainers" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium transition-colors" onClick={() => setMobileMenuOpen(false)}>Trainers</Link>
              <Link to="/pricing" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium transition-colors" onClick={() => setMobileMenuOpen(false)}>Pricing</Link>
              <div className="pt-3 border-t border-gray-200 mt-3">
                {user ? (
                  <>
                    <Link to={`/dashboard/${user.role}`} className="block py-3 px-4 bg-navy text-white rounded-lg font-medium text-center mb-2" onClick={() => setMobileMenuOpen(false)}>
                      Dashboard
                    </Link>
                    <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="block w-full py-3 px-4 border border-navy text-navy rounded-lg font-medium text-center">
                      Logout
                    </button>
                  </>
                ) : (
                  <Link to="/?login=true" className="block py-3 px-4 bg-navy text-white rounded-lg font-medium text-center" onClick={() => setMobileMenuOpen(false)}>
                    Sign In
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative text-white py-20 mt-20 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('https://images.pexels.com/photos/3080665/pexels-photo-3080665.jpeg?w=1920&q=80')" }}
        />
        <div className="absolute inset-0 bg-navy/80" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="inline-block bg-gold/10 text-gold border border-gold/30 px-6 py-2 rounded-full text-sm font-bold uppercase tracking-widest mb-6">
              CORPORATE GIFTING
            </span>
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
              Premium Corporate Gifts for Every Occasion
            </h1>
            <p className="text-white/90 text-lg max-w-3xl mx-auto">
              Curated collection of 52+ premium corporate gifts - from executive items to eco-friendly options
            </p>
          </div>
        </div>
      </div>

      {/* Gifts Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12"> 

        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2" data-testid="gifts-page-title">
                Premium Corporate Gifts
              </h1>
              <p className="text-lg text-slate-800">
                Curated gift collections for your business events and celebrations
              </p>
            </div>
            <Button 
              onClick={() => setShowVendorDialog(true)}
              className="bg-gold hover:bg-gold-light text-navy-900 font-semibold"
            >
              <Package className="w-4 h-4 mr-2" />
              List Your Products
            </Button>
          </div>
        </div>

        {/* Stats Banner */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-4 rounded-lg border border-slate-200 text-center">
            <p className="text-3xl font-bold text-navy-800">{gifts.length}+</p>
            <p className="text-sm text-slate-800">Products</p>
          </div>
          <div className="bg-white p-4 rounded-lg border border-slate-200 text-center">
            <p className="text-3xl font-bold text-navy-800">8</p>
            <p className="text-sm text-slate-800">Categories</p>
          </div>
          <div className="bg-white p-4 rounded-lg border border-slate-200 text-center">
            <p className="text-3xl font-bold text-navy-800">50+</p>
            <p className="text-sm text-slate-800">Vendors</p>
          </div>
          <div className="bg-white p-4 rounded-lg border border-slate-200 text-center">
            <p className="text-3xl font-bold text-navy-800">5-7</p>
            <p className="text-sm text-slate-800">Days Delivery</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg border border-slate-200 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-700" />
              <Input
                placeholder="Search gifts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger>
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label} ({getCategoryCount(cat.value)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={priceRange} onValueChange={setPriceRange}>
              <SelectTrigger>
                <SelectValue placeholder="Price Range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Prices</SelectItem>
                <SelectItem value="0-2000">Under ₹2,000</SelectItem>
                <SelectItem value="2000-5000">₹2,000 - ₹5,000</SelectItem>
                <SelectItem value="5000-10000">₹5,000 - ₹10,000</SelectItem>
                <SelectItem value="10000-999999">Above ₹10,000</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              onClick={() => {
                setSelectedCategory('all');
                setSearchQuery('');
                setPriceRange('all');
              }}
              className="border-slate-300"
            >
              Clear Filters
            </Button>
          </div>
        </div>

        {/* Category Pills */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map(cat => (
            <button
              key={cat.value}
              onClick={() => setSelectedCategory(cat.value)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                selectedCategory === cat.value
                  ? 'bg-navy-600 text-white'
                  : 'bg-white text-slate-700 border border-slate-200 hover:border-navy-300'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Results Count */}
        <p className="text-slate-800 mb-6">
          Showing {filteredGifts.length} of {gifts.length} products
        </p>

        {/* Gift Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredGifts.map((gift) => (
            <div key={gift.id} className="bg-white rounded-lg overflow-hidden border border-slate-200 hover:shadow-lg transition-all group" data-testid={`gift-card-${gift.id}`}>
              <div className="relative">
                <img
                  src={gift.images[0]}
                  alt={gift.name}
                  className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <span className="absolute top-3 left-3 bg-navy-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                  {gift.category}
                </span>
                {/* All gifts are now customizable */}
                <span className="absolute top-3 right-3 bg-gold text-navy-900 text-xs font-semibold px-2 py-1 rounded-full flex items-center gap-1">
                  <Palette className="w-3 h-3" />
                  Customizable
                </span>
              </div>
              <div className="p-5">
                <h3 className="font-semibold text-slate-900 mb-2 line-clamp-1">{gift.name}</h3>
                <p className="text-sm text-slate-800 line-clamp-2 mb-3">{gift.description}</p>
                
                <div className="flex items-center gap-2 text-xs text-slate-700 mb-3">
                  <Tag className="w-3 h-3" />
                  <span>SKU: {gift.sku || 'N/A'}</span>
                </div>
                
                <div className="flex items-center justify-between mb-4">
                  <span className="text-2xl font-bold text-navy-800">
                    ₹{gift.price.toLocaleString()}
                  </span>
                  <span className="text-xs text-slate-700">
                    Min: {gift.min_order_quantity || 1} units
                  </span>
                </div>
                
                <div className="flex items-center gap-2 text-xs text-slate-700 mb-4">
                  <Truck className="w-3 h-3" />
                  <span>{gift.delivery_time || '5-7 days'}</span>
                  <span className="text-green-600">• {gift.stock} in stock</span>
                </div>
                
                {/* Primary CTA - Customize & Order */}
                <Button
                  onClick={() => {
                    if (!user) {
                      toast.info('Please login to customize and order gifts');
                      setPendingAction('customize');
                      setPendingGift(gift);
                      setShowLoginDialog(true);
                      return;
                    }
                    setSelectedGift(gift);
                    setShowCustomizationModal(true);
                  }}
                  className="w-full bg-gold hover:bg-gold-light text-navy-900 font-semibold mb-2"
                  data-testid={`customize-gift-button-${gift.id}`}
                >
                  <Palette className="w-4 h-4 mr-2" />
                  Customize & Order
                </Button>
                
                {/* Secondary CTA - Quick Order without customization */}
                <Button
                  variant="outline"
                  onClick={() => {
                    if (!user) {
                      toast.info('Please login to order gifts');
                      setPendingAction('quickOrder');
                      setPendingGift(gift);
                      setShowLoginDialog(true);
                      return;
                    }
                    setSelectedGift(gift);
                    setShowOrderDialog(true);
                  }}
                  className="w-full border-navy-600 text-navy-600 hover:bg-navy-50"
                  data-testid={`order-gift-button-${gift.id}`}
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Quick Order
                </Button>
              </div>
            </div>
          ))}
        </div>

        {filteredGifts.length === 0 && (
          <div className="text-center py-16">
            <Gift className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No gifts found</h3>
            <p className="text-slate-700">Try adjusting your filters or search query</p>
          </div>
        )}
      </div>

      {/* Order Dialog */}
      <Dialog open={showOrderDialog} onOpenChange={setShowOrderDialog}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="gift-order-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-slate-900">Complete Your Order</DialogTitle>
            <DialogDescription className="text-slate-800">
              {selectedGift && `Ordering: ${selectedGift.name}`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleOrder} className="space-y-4">
            <div>
              <Label htmlFor="quantity" className="text-slate-900">Quantity (Min: {selectedGift?.min_order_quantity || 1})</Label>
              <Input 
                id="quantity" 
                name="quantity" 
                type="number" 
                min={selectedGift?.min_order_quantity || 1} 
                defaultValue={selectedGift?.min_order_quantity || 1} 
                required 
                data-testid="gift-quantity-input" 
              />
            </div>
            <div>
              <Label htmlFor="delivery_name" className="text-slate-900">Recipient Name</Label>
              <Input id="delivery_name" name="delivery_name" required data-testid="gift-name-input" />
            </div>
            <div>
              <Label htmlFor="delivery_phone" className="text-slate-900">Recipient Phone</Label>
              <Input id="delivery_phone" name="delivery_phone" required data-testid="gift-phone-input" />
            </div>
            <div>
              <Label htmlFor="delivery_address" className="text-slate-900">Delivery Address</Label>
              <Textarea id="delivery_address" name="delivery_address" required data-testid="gift-address-textarea" />
            </div>
            <div className="bg-slate-50 p-4 rounded-lg">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-slate-800">Unit Price:</span>
                <span className="text-slate-900">₹{selectedGift?.price?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-800">Delivery:</span>
                <span className="text-slate-900">{selectedGift?.delivery_time || '5-7 days'}</span>
              </div>
            </div>
            
            {/* Test Mode for Payment Testing */}
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="giftTestMode" 
                  checked={testMode}
                  onCheckedChange={(checked) => setTestMode(checked)}
                />
                <Label htmlFor="giftTestMode" className="flex items-center gap-2 cursor-pointer">
                  <TestTube2 className="w-4 h-4 text-amber-600" />
                  <span className="text-amber-800 font-medium">Test Mode (Custom Amount)</span>
                </Label>
              </div>
              {testMode && (
                <div className="space-y-2">
                  <Label htmlFor="giftCustomAmount" className="text-amber-700 text-sm">
                    Enter custom payment amount (₹)
                  </Label>
                  <Input 
                    id="giftCustomAmount"
                    type="number"
                    min="1"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(parseFloat(e.target.value) || 1)}
                    className="bg-white border-amber-300 focus:border-amber-500"
                    placeholder="Enter amount in rupees"
                  />
                  <p className="text-xs text-amber-600">
                    ⚠️ Test payment will be processed for ₹{customAmount} instead of actual order cost
                  </p>
                </div>
              )}
            </div>
            
            {/* Razorpay Verified Badge */}
            <div className="flex flex-col items-center gap-3 py-4 px-4 bg-gray-50 rounded-lg border border-gray-200">
              <span className="text-sm text-slate-900 font-semibold">Secure Payments Powered By</span>
              <div className="flex items-center gap-4">
                <img 
                  src="https://cdn.razorpay.com/logo.svg" 
                  alt="Razorpay" 
                  className="h-5"
                />
                <div className="w-px h-6 bg-gray-300"></div>
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg" 
                  alt="Visa" 
                  className="h-4"
                />
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" 
                  alt="Mastercard" 
                  className="h-6"
                />
                <div className="w-px h-6 bg-gray-300"></div>
                <img 
                  src="https://badges.razorpay.com/badge-dark.png" 
                  alt="Razorpay Trusted Business" 
                  className="h-7"
                />
              </div>
            </div>
            
            <Button type="submit" className="w-full bg-gold hover:bg-gold-light text-navy-900 font-semibold" data-testid="gift-order-confirm-button">
              Proceed to Payment
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Vendor Listing Dialog */}
      <Dialog open={showVendorDialog} onOpenChange={setShowVendorDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-slate-900">List Your Products on My Corporate Hub</DialogTitle>
            <DialogDescription className="text-slate-800">
              Reach thousands of corporate buyers across India
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Benefits */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-slate-900">500+ Corporates</p>
                  <p className="text-sm text-slate-800">Access premium corporate buyers</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-slate-900">Bulk Orders</p>
                  <p className="text-sm text-slate-800">High-volume corporate orders</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-slate-900">Easy Management</p>
                  <p className="text-sm text-slate-800">Vendor dashboard for SKU management</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-slate-900">Secure Payments</p>
                  <p className="text-sm text-slate-800">Guaranteed payment protection</p>
                </div>
              </div>
            </div>

            {/* How to List */}
            <div className="bg-slate-50 p-6 rounded-lg">
              <h3 className="font-bold text-slate-900 mb-4">How to List Your SKUs:</h3>
              <ol className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-navy-600 text-white text-sm font-bold rounded-full flex items-center justify-center flex-shrink-0">1</span>
                  <div>
                    <p className="font-semibold text-slate-900">Register as a Vendor</p>
                    <p className="text-sm text-slate-800">Sign up with your business details and GST number</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-navy-600 text-white text-sm font-bold rounded-full flex items-center justify-center flex-shrink-0">2</span>
                  <div>
                    <p className="font-semibold text-slate-900">Add Your Products</p>
                    <p className="text-sm text-slate-800">Upload product images, descriptions, SKU codes, pricing, and stock levels</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-navy-600 text-white text-sm font-bold rounded-full flex items-center justify-center flex-shrink-0">3</span>
                  <div>
                    <p className="font-semibold text-slate-900">Set Bulk Pricing</p>
                    <p className="text-sm text-slate-800">Configure minimum order quantities and volume discounts</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-navy-600 text-white text-sm font-bold rounded-full flex items-center justify-center flex-shrink-0">4</span>
                  <div>
                    <p className="font-semibold text-slate-900">Start Selling</p>
                    <p className="text-sm text-slate-800">Receive orders and manage fulfillment through your dashboard</p>
                  </div>
                </li>
              </ol>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                onClick={() => {
                  setShowVendorDialog(false);
                  navigate('/register?role=vendor');
                }}
                className="flex-1 bg-navy-600 hover:bg-navy-700 text-white font-semibold"
              >
                Register as Vendor
              </Button>
              <Button 
                variant="outline"
                onClick={() => {
                  setShowVendorDialog(false);
                  if (user?.role === 'vendor') {
                    navigate('/dashboard/vendor');
                  } else {
                    toast.info('Please login as a vendor to access the dashboard');
                  }
                }}
                className="flex-1 border-navy-600 text-navy-600 hover:bg-navy-50"
              >
                Vendor Dashboard
              </Button>
            </div>

            {/* Contact Info */}
            <div className="text-center pt-4 border-t">
              <p className="text-sm text-slate-800">
                Need help? Contact our vendor support team at{' '}
                <a href="mailto:vendors@mycorporatehub.com" className="text-navy-600 font-semibold hover:underline">
                  vendors@mycorporatehub.com
                </a>
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Gift Customization Modal (Vistaprint-style) */}
      <GiftCustomizationModal
        isOpen={showCustomizationModal}
        onClose={() => {
          setShowCustomizationModal(false);
          setSelectedGift(null);
        }}
        gift={selectedGift}
        onOrderComplete={(order) => {
          toast.success('Order placed successfully!');
          setShowCustomizationModal(false);
          setSelectedGift(null);
        }}
      />
      
      {/* Footer */}
      <Footer />

      {/* Fixed Rotating Logo - Bottom Left */}
      <ScrollingLogo />

      {/* Login Dialog */}
      <Dialog open={showLoginDialog} onOpenChange={(open) => {
        setShowLoginDialog(open);
        if (!open) {
          setLoginEmail('');
          setLoginPassword('');
          setShowPassword(false);
          setPendingAction(null);
          setPendingGift(null);
        }
      }}>
        <DialogContent className="sm:max-w-md bg-white">
          {/* Close Button */}
          <button
            onClick={() => setShowLoginDialog(false)}
            className="absolute right-4 top-4 rounded-full p-1 hover:bg-slate-100 transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-slate-500 hover:text-slate-700" />
          </button>
          
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-navy">Welcome Back</DialogTitle>
            <p className="text-slate-800 text-sm">Sign in to customize and order gifts</p>
          </DialogHeader>
          
          <form onSubmit={handleLogin} className="space-y-4 mt-4">
            <div>
              <Input
                type="email"
                placeholder="Email address"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                required
                className="h-12 bg-white border-gray-300 text-navy placeholder:text-gray-700"
              />
            </div>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                required
                className="h-12 bg-white border-gray-300 text-navy placeholder:text-gray-700 pr-12"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 transition-colors"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            <Button type="submit" className="w-full bg-gold text-navy hover:bg-gold-light h-12 font-semibold" disabled={isLoading}>
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
          
          <div className="text-center mt-4">
            <p className="text-sm text-slate-600">
              Don't have an account?{' '}
              <Link to="/" className="text-gold hover:underline font-medium">
                Sign up on homepage
              </Link>
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
